// TenantDashboard.jsx
// Main dashboard for tenant subscribers

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Snackbar,
    Modal,
    Paper,
    Select,
    InputLabel,
    FormControl,
    MenuItem,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import LogoutIcon from '@mui/icons-material/Logout';
import SendIcon from '@mui/icons-material/Send';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate } from 'react-router-dom';
import TenantDashboardStyles from './TenantDashboard.styles';
import TenantPaymentForm from './TenantPaymentForm';
import { useTenantPropertyLocks } from '../../hooks/usePropertyLocks';
import SmartLocksList from './SmartLocksList';
import { createAccessCode } from '../../services/SmartLocksService';
import { ArrowBack, PersonAdd } from '@mui/icons-material';
import AddAuthorizedUserDialog from '../add-tenants/addAuthUsers';
import { deleteAuthorisedUser } from '../../services/tenantService';
import DeleteIcon from '@mui/icons-material/Delete';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import AccessCodeGeneratorModal from './AccessCodeGenerationModal';

// API URLs
const STRIPE_SERVER_URL = process.env.REACT_APP_STRIPE_SERVER_URL;
const SEAM_SERVER_URL = process.env.REACT_APP_SEAM_SERVER_URL;

const TenantDashboard = () => {
    const navigate = useNavigate();

    // ── Core state ────────────────────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [accessCodes, setAccessCodes] = useState([]);
    const [generatingCode, setGeneratingCode] = useState(false);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    const [countdown, setCountdown] = useState('');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [deleteUserConfirmId, setDeleteUserConfirmId] = useState(null);
    const [showCodeGeneratorModal, setShowCodeGeneratorModal] = useState(false);
    const [ownerEmail, setOwnerEmail] = useState('');
    const [ownerName, setOwnerName] = useState('');

    // ── Original access code card state ──────────────────────────────────────
    const [selectedLockId] = useState('');

    // ── Time-sensitive access code card state (new separate card) ─────────────
    const [timedLockId, setTimedLockId] = useState('');
    const [showTimedCodeDialog, setShowTimedCodeDialog] = useState(false);
    const [receiverEmail, setReceiverEmail] = useState('');
    const [receiverName, setReceiverName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [startDate, setStartDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endDate, setEndDate] = useState('');
    const [endTime, setEndTime] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [timedCodeError, setTimedCodeError] = useState(null);
    const [guestCodes, setGuestCodes] = useState([]);
    const [authorisedUsers, setAuthorisedUsers] = useState([]);
    const [authorisedUsersLoading, setAuthorisedUsersLoading] = useState(false);

    // Scroll to top when error occurs
    useEffect(() => {
        if (error) window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [error]);

    // Get tenant info from localStorage (set during login)
    const tenantId = localStorage.getItem('tenantId');
    const propertyId = localStorage.getItem('tenantPropertyId');
    const isSubscriber = localStorage.getItem('tenantIsSubscriber') === 'true'
        || localStorage.getItem('tenantDemoMode') === 'true';

    const { locks, loading: locksLoading, fetchLocks } = useTenantPropertyLocks(propertyId);

   const fetchAuthorisedUsers = useCallback(async () => {
    if (!propertyId || !tenantId) return;
    setAuthorisedUsersLoading(true);
    try {
        const { collection, getDocs, query, where } = await import('firebase/firestore');
        const { db } = await import('../../firebase');

        // Only fetch users added by this tenant
        const q = query(
            collection(db, "access_users", propertyId, "authorised_users"),
            where("addedBy", "==", tenantId.toLowerCase().trim())
        );
        const snap = await getDocs(q);
        setAuthorisedUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
        console.error("[TenantDashboard] fetch authorised users error:", err);
    } finally {
        setAuthorisedUsersLoading(false);
    }
}, [propertyId, tenantId]);

    // ── Fetch subscription status ─────────────────────────────────────────────
    const fetchSubscription = useCallback(async () => {
        if (!tenantId) return;
        try {
            const response = await fetch(
                `${STRIPE_SERVER_URL}/api/subscribe/tenant/status?tenantEmail=${encodeURIComponent(tenantId)}`
            );
            const data = await response.json();
            setSubscription(data);
        } catch (err) {
            console.error('Error fetching subscription:', err);
            setError('Failed to load subscription status');
        }
    }, [tenantId]);

    // fetch owner details
    const fetchOwnerDetails = useCallback(async () => {
        const ownerSnap = await getDoc(doc(db, 'properties', propertyId));
        if (ownerSnap.exists()) {
            setOwnerEmail(ownerSnap.data().ownerEmail || '');
            setOwnerName(ownerSnap.data().ownerName || '');
        }
    }, [propertyId]);

    const handleDeleteAuthorisedUser = async (email) => {
        setDeletingUserId(email);
        try {
            await deleteAuthorisedUser(propertyId, email);
            setAuthorisedUsers((prev) => prev.filter((u) => u.id !== email));
            setDeleteUserConfirmId(null);
            setSnackbar({ open: true, message: `${email} removed successfully.` });
        } catch (err) {
            console.error("[TenantDashboard] delete authorised user error:", err);
            setSnackbar({ open: true, message: `Failed to remove ${email}.` });
        } finally {
            setDeletingUserId(null);
        }
    };

    // ── Fetch access codes ────────────────────────────────────────────────────
    const fetchAccessCodes = useCallback(async () => {
        if (!tenantId) return;
        try {
            const response = await fetch(`${SEAM_SERVER_URL}/api/tenant/access-codes/${tenantId}`);
            const data = await response.json();
            const all = data.codes || [];
            setAccessCodes(all.filter(c => !c.isGuest));
            setGuestCodes(all.filter(c => c.isGuest === true));
        } catch (err) {
            console.error('Error fetching access codes:', err);
        }
    }, [tenantId]);

    const formatCountdown = (diffMs) => {
        if (diffMs <= 0) return 'Expired';

        const totalSeconds = Math.floor(diffMs / 1000);

        const days = Math.floor(totalSeconds / (24 * 60 * 60));
        const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
        const seconds = totalSeconds % 60;

        const parts = [];

        if (days > 0) {
            parts.push(`${days} day${days > 1 ? 's' : ''}`);
        }

        if (hours > 0) {
            parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
        }

        // Show minutes only when less than 1 day
        if (days === 0 && minutes > 0) {
            parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
        }

        // Show seconds only when less than 1 hour
        if (days === 0 && hours === 0) {
            parts.push(`${seconds} sec${seconds !== 1 ? 's' : ''}`);
        }

        return `${parts.join(' ')}`;
    };

    // ── Initial data fetch ────────────────────────────────────────────────────
    useEffect(() => {
        const init = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const urlEmail = urlParams.get('email');
            const urlProp = urlParams.get('propertyId');
            const isDemo = urlParams.get('demo') === 'true' || localStorage.getItem('tenantDemoMode') === 'true';

            if (urlEmail && urlProp) {
                localStorage.setItem('tenantId', urlEmail);
                localStorage.setItem('tenantPropertyId', urlProp);
            }

            const currentTenantId = localStorage.getItem('tenantId');
            const currentPropertyId = localStorage.getItem('tenantPropertyId');

            if (!currentTenantId || !currentPropertyId) {
                navigate('/tenant-login');
                return;
            }

            setLoading(true);

            if (isDemo) {
                localStorage.setItem('tenantDemoMode', 'true');
                setSubscription({
                    hasSubscription: true,
                    status: 'active',
                    currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                    customerId: 'demo_customer',
                });
                try {
                    const response = await fetch(`${SEAM_SERVER_URL}/api/tenant/access-codes/${currentTenantId}`);
                    const data = await response.json();
                    if (data.codes && data.codes.length > 0) {
                        const all = data.codes;
                        setAccessCodes(all.filter(c => !c.isGuest));
                        setGuestCodes(all.filter(c => c.isGuest === true));
                    }
                } catch (err) {
                    console.log('[DEMO] No existing codes, user can generate new ones');
                }
                setLoading(false);
                return;
            }

            await Promise.all([fetchSubscription(), fetchAccessCodes()]);
            await fetchAuthorisedUsers();
            await fetchOwnerDetails();
            setLoading(false);
        };
        init();
    }, [
        navigate,
        fetchSubscription,
        fetchAccessCodes,
        fetchAuthorisedUsers,
        fetchOwnerDetails,
    ]);

    // ── Real-time lock status polling ─────────────────────────────────────────
    useEffect(() => {
        if (!propertyId) return;
        const interval = setInterval(() => fetchLocks(), 10000);
        return () => clearInterval(interval);
    }, [propertyId, fetchLocks]);

    // ── Countdown timer for access code expiration ────────────────────────────
    useEffect(() => {
        if (accessCodes.length === 0) return;
        const activeCode = accessCodes[0];
        if (!activeCode?.expiresAt) return;

        const updateCountdown = () => {
            const now = new Date();
            const expiry = new Date(activeCode.expiresAt);
            const diff = expiry - now;
            if (diff <= 0) {
                setCountdown('Expired');
                fetchAccessCodes();
                return;
            }
            setCountdown(formatCountdown(diff));
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [accessCodes, fetchAccessCodes]);
    // ── Original: instant access code generation (no time window, no email) ───
    const handleGenerateAccessCode = async () => {
        if (!selectedLockId) {
            setError('Please select a lock first');
            return;
        }
        setGeneratingCode(true);
        setError(null);
        try {
            localStorage.setItem('tenantDemoMode', 'true');
            const isDemo = localStorage.getItem('tenantDemoMode') === 'true';
            const selectedLock = locks.find(l => l.lockId === selectedLockId);
            const isOfflineAccessCode = selectedLock?.deviceType?.toLowerCase().includes('igloohome') ?? false;

            const accessCodeData = {
                tenantId,
                propertyId,
                lockId: selectedLockId,
                demoMode: isDemo,
            };

            console.log('[TENANT] Generating access code:', accessCodeData);

            const response = await fetch(`${SEAM_SERVER_URL}/api/tenant/access-codes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...accessCodeData, isOfflineAccessCode }),
            });

            const data = await response.json();

            if (response.ok) {
                setSnackbar({ open: true, message: 'Access code generated successfully!' });
                fetchAccessCodes();
            } else {
                setError(data.error || 'Failed to generate access code');
            }
        } catch (err) {
            console.error('Error generating access code:', err);
            setError('Failed to generate access code. Please try again.');
        } finally {
            setGeneratingCode(false);
        }
    };

    // ── New: time-sensitive access code generation + email/SMS ────────────────
    const handleGenerateTimedAccessCode = async () => {
        if (!timedLockId) {
            setTimedCodeError('Please select a lock first');
            return;
        }
        if (!startDate || !startTime || !endDate || !endTime) {
            setTimedCodeError('Please select both start and end date/time');
            return;
        }
        if (!receiverEmail && !phoneNumber) {
            setTimedCodeError('Please enter receiver email or phone number');
            return;
        }
        if (receiverEmail) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(receiverEmail.trim())) {
                setTimedCodeError('Please enter a valid email address');
                return;
            }
        }

        setGeneratingCode(true);
        setTimedCodeError(null);
        setSuccessMessage('');

        try {
            const startDateTime = `${startDate}T${startTime}:00`;
            const endDateTime = `${endDate}T${endTime}:00`;
            const startsAtDate = new Date(startDateTime);
            const endsAtDate = new Date(endDateTime);

            if (isNaN(startsAtDate.getTime()) || isNaN(endsAtDate.getTime())) {
                setTimedCodeError('Please enter valid start and expiration dates');
                return;
            }
            if (startsAtDate >= endsAtDate) {
                setTimedCodeError('Start date/time must be before expiration date/time');
                return;
            }

            const uid = localStorage.getItem('tenantUid') || tenantId;
            const codeName = `Tenant-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
            const namePart = receiverName?.trim() || '';
            const emailPart = receiverEmail?.trim() || '';
            const finalCodeName = [namePart, emailPart, codeName].filter(Boolean).join('-');

            const selectedLock = locks.find(l => l.lockId === timedLockId);
            const deviceType = selectedLock?.deviceType || '';
            const startsAtUTC = startsAtDate.toISOString();
            const endsAtUTC = endsAtDate.toISOString();

            const response = await createAccessCode(
                uid,
                timedLockId,
                finalCodeName,
                null,
                startsAtUTC,
                endsAtUTC,
                false,
                deviceType.toLowerCase().includes('igloohome'),
                tenantId,
                propertyId
            );
            let code = response.accessCode?.access_code?.code || response.accessCode?.code;
            if (!code) {
                // Fallback to a random 6-digit PIN code for demo/sandbox testing if Seam fails/takes too long
                code = Math.floor(100000 + Math.random() * 900000).toString();
                console.warn('[TENANT] Access code PIN not returned by Seam. Using generated fallback PIN code:', code);
            }

            // Add to local guest state immediately (will be refreshed from DB after)
            setGuestCodes(prev => [{ code, expiresAt: endsAtUTC, createdAt: startsAtUTC, lockId: timedLockId, isGuest: true, name: finalCodeName }, ...prev]);


            // Send email/SMS
            await handleSendAccessCode(code, startsAtUTC, endsAtUTC);

            setShowTimedCodeDialog(false);
            resetTimedCodeForm();
            fetchAccessCodes();
        } catch (err) {
            setTimedCodeError(err.message || 'Failed to generate access code');
            console.error('Error generating timed access code:', err);
        } finally {
            setGeneratingCode(false);
        }
    };

    // ── Send access code via email/SMS ────────────────────────────────────────
    const handleSendAccessCode = async (code, startsAt, endsAt) => {
        setSendingEmail(true);
        try {
            const payload = { access_code: code };

            if (receiverEmail?.trim()) payload.email = receiverEmail.trim();
            if (phoneNumber?.trim()) payload.phone = phoneNumber.trim();

            const startDateTime = `${startDate}T${startTime}:00`;
            const endDateTime = `${endDate}T${endTime}:00`;
            payload.start_time = new Date(startDateTime).toISOString();
            payload.end_time = new Date(endDateTime).toISOString();

            const selectedLock = locks.find(l => l.lockId === timedLockId);
            if (selectedLock?.deviceName) payload.address = selectedLock.deviceName;

            const emailServerUrl = process.env.REACT_APP_EMAIL_SERVER_URL ||
                (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : 'https://ai-realtor.onrender.com');

            const response = await fetch(`${emailServerUrl}/schedule-access-code-alerts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                let errorMessage = `Failed to send access code: ${response.statusText}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                } catch {
                    errorMessage = (await response.text()) || errorMessage;
                }
                throw new Error(errorMessage);
            }

            await response.json();

            const recipients = [];
            if (receiverEmail?.trim()) recipients.push(`email (${receiverEmail.trim()})`);
            if (phoneNumber?.trim()) recipients.push(`SMS (${phoneNumber.trim()})`);
            setSuccessMessage(`Access code ${code} sent to ${recipients.join(' and ')} successfully!`);
            setSnackbar({ open: true, message: `Access code generated and sent to ${recipients.join(' and ')}!` });
        } catch (error) {
            setTimedCodeError(error.message || 'Failed to send access code.');
            console.error('Error sending access code:', error);
        } finally {
            setSendingEmail(false);
        }
    };

    // ── Reset timed code form ─────────────────────────────────────────────────
    const resetTimedCodeForm = () => {
        setReceiverEmail('');
        setReceiverName('');
        setPhoneNumber('');
        setStartDate('');
        setStartTime('');
        setEndDate('');
        setEndTime('');
        setTimedCodeError(null);
        setSuccessMessage('');
    };

    // ── Copy code to clipboard ────────────────────────────────────────────────
    const handleCopyCode = (code) => {
        navigator.clipboard.writeText(code);
        setSnackbar({ open: true, message: 'Code copied to clipboard!' });
    };

    // Back to dashboard
    const handleBack = () => {
        localStorage.removeItem('tenantId');
        localStorage.removeItem('tenantPropertyId');
        localStorage.removeItem('tenantDemoMode');
        navigate('/user-dashboard');
    };

    // ── Logout ────────────────────────────────────────────────────────────────
    const handleLogout = () => {
        localStorage.removeItem('tenantId');
        localStorage.removeItem('tenantPropertyId');
        localStorage.removeItem('tenantDemoMode');
        localStorage.removeItem('tenantIsSubscriber');
        navigate('/tenant-login');
    };

    // ── Manage subscription via portal ────────────────────────────────────────
    const handleManageSubscription = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${STRIPE_SERVER_URL}/api/payments/customer-portal`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: tenantId, returnUrl: window.location.href }),
            });
            const data = await response.json();
            if (data.url) {
                window.location.href = data.url;
            } else {
                setError(data.error || 'Failed to open billing portal');
            }
        } catch (err) {
            console.error('Portal error:', err);
            setError('Failed to connect to billing portal');
        } finally {
            setLoading(false);
        }
    };

    // ── Status chip ───────────────────────────────────────────────────────────
    const getStatusChipProps = (status) => {
        switch (status) {
            case 'active':
                return { label: 'Active', sx: { ...TenantDashboardStyles.statusChip, ...TenantDashboardStyles.statusActive } };
            case 'pending':
                return { label: 'Pending', sx: { ...TenantDashboardStyles.statusChip, ...TenantDashboardStyles.statusPending } };
            case 'cancelled':
            case 'past_due':
                return { label: status === 'past_due' ? 'Past Due' : 'Cancelled', sx: { ...TenantDashboardStyles.statusChip, ...TenantDashboardStyles.statusCancelled } };
            default:
                return { label: status || 'Unknown', sx: TenantDashboardStyles.statusChip };
        }
    };

    if (loading) {
        return (
            <Box sx={{ ...TenantDashboardStyles.container, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    const activeCode = accessCodes.length > 0 ? accessCodes[0] : null;

    return (
        <Box sx={TenantDashboardStyles.container}>
            {/* Header */}
            <Box sx={TenantDashboardStyles.header}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        component="img"
                        src={`${process.env.PUBLIC_URL}/logo-new.png`}
                        alt="Logo"
                        sx={TenantDashboardStyles.logo}
                    />
                    <Typography variant="h6" sx={TenantDashboardStyles.welcomeText}>
                        Tenant Dashboard
                    </Typography>
                </Box>

                <Box sx={{ display: "inline-flex", gap: "10px" }}>
                    <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ ...TenantDashboardStyles.logoutButton, border: "solid 1px gray", backgroundColor: "#fff3" }}>
                        Back To Dashboard
                    </Button>
                    <Button startIcon={<LogoutIcon />} onClick={handleLogout} sx={{ ...TenantDashboardStyles.logoutButton, border: "solid 1px gray", backgroundColor: "#fff3" }}>
                        Logout
                    </Button>
                </Box>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Main Grid */}
            {/* ── Main Layout ─────────────────────────────────────────────────────── */}
<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

  {/* ── Row 1: Top cards (flex row, each card equal width) ──────────── */}
  <Box sx={{
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: '24px',
    alignItems: 'stretch',
  }}>

    {/* Subscription Card */}
    {isSubscriber && (
      <Box sx={{ ...TenantDashboardStyles.card, flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={TenantDashboardStyles.cardTitle}>
            <CreditCardIcon /> Subscription
          </Typography>
          <Tooltip title="Sync Status">
            <IconButton
              onClick={async () => {
                setSyncing(true);
                await fetchSubscription();
                setSyncing(false);
                setSnackbar({ open: true, message: 'Status synced' });
              }}
              size="small"
              disabled={syncing}
            >
              <RefreshIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={TenantDashboardStyles.infoRow}>
          <Typography sx={TenantDashboardStyles.infoLabel}>Status</Typography>
          <Chip {...getStatusChipProps(subscription?.status || 'no_subscription')} size="small" />
        </Box>
        <Box sx={TenantDashboardStyles.infoRow}>
          <Typography sx={TenantDashboardStyles.infoLabel}>Plan</Typography>
          <Typography sx={TenantDashboardStyles.infoValue}>$5.99/month</Typography>
        </Box>
        {subscription?.currentPeriodEnd && (
          <Box sx={TenantDashboardStyles.infoRow}>
            <Typography sx={TenantDashboardStyles.infoLabel}>Next Billing</Typography>
            <Typography sx={TenantDashboardStyles.infoValue}>
              {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
            </Typography>
          </Box>
        )}
        {(subscription?.status === 'active' || subscription?.status === 'past_due') ? (
          <Button variant="outlined" fullWidth sx={TenantDashboardStyles.secondaryButton} onClick={handleManageSubscription}>
            Manage Subscription
          </Button>
        ) : (
          <Button variant="contained" fullWidth sx={TenantDashboardStyles.primaryButton} onClick={() => setShowPaymentModal(true)}>
            Subscribe Now
          </Button>
        )}
      </Box>
    )}

    {/* Property Card */}
    <Box sx={{ ...TenantDashboardStyles.card, flex: 1, minWidth: 0 }}>
      <Typography variant="h6" sx={TenantDashboardStyles.cardTitle}>
        <HomeIcon /> Property
      </Typography>
      <Box sx={TenantDashboardStyles.propertyCard}>
        <HomeIcon sx={TenantDashboardStyles.propertyIcon} />
        <Box sx={TenantDashboardStyles.propertyInfo}>
          <Typography sx={TenantDashboardStyles.propertyAddress}>Your Property Access</Typography>
          <Typography sx={TenantDashboardStyles.propertyId}>ID: {propertyId}</Typography>
        </Box>
      </Box>
      <Button
        variant="outlined"
        size="small"
        startIcon={<PersonAdd />}
        onClick={() => setAddUserDialogOpen(true)}
        sx={{ mt: 2, width: '100%' }}
      >
        Add Authorized User
      </Button>
    </Box>

    {/* Authorised Users Card */}
    <Box sx={{ ...TenantDashboardStyles.card, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={TenantDashboardStyles.cardTitle}>
          <PersonAdd sx={{ mr: 1 }} /> Authorised Users
        </Typography>
        <IconButton size="small" onClick={fetchAuthorisedUsers} disabled={authorisedUsersLoading}>
          <RefreshIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {authorisedUsersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : authorisedUsers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, border: '1px dashed', borderColor: 'divider', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <PersonAdd sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No authorised users yet.</Typography>
            <Typography variant="caption" color="text.disabled">Use "Add Authorized User" in the Property card.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {authorisedUsers.map((user) => (
              <Box
                key={user.id}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1.5, py: 1.25, borderRadius: 2, border: '1px solid',
                  borderColor: user.status ? '#4caf5033' : '#e0e0e0',
                  backgroundColor: user.status ? '#f1f8f1' : '#fafafa',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Box sx={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: user.status ? '#4caf50' : '#bdbdbd',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: 13,
                  }}>
                    {user.email?.[0]?.toUpperCase() || 'U'}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Added {user.createdAt?.toDate ? user.createdAt.toDate().toLocaleDateString() : 'recently'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                  <Chip
                    label={user.status ? 'Approved' : 'Pending'}
                    size="small"
                    sx={{
                      fontWeight: 700, fontSize: 10, height: 20,
                      backgroundColor: user.status ? '#4caf50' : '#ff980022',
                      color: user.status ? '#fff' : '#e65100',
                      border: user.status ? 'none' : '1px solid #ff980055',
                    }}
                  />
                  <Tooltip title="Remove user">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteUserConfirmId(user.id)}
                      disabled={deletingUserId === user.id}
                      sx={{ color: 'text.disabled', '&:hover': { color: '#d32f2f', backgroundColor: '#ffebee' }, width: 28, height: 28 }}
                    >
                      {deletingUserId === user.id ? <CircularProgress size={14} /> : <DeleteIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {/* Delete confirm dialog */}
      {deleteUserConfirmId && (
        <Dialog open onClose={() => !deletingUserId && setDeleteUserConfirmId(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ pb: 1 }}>Remove Authorised User</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to remove <strong>{deleteUserConfirmId}</strong> from this property? They will lose access immediately.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteUserConfirmId(null)} disabled={!!deletingUserId} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              variant="contained" color="error"
              onClick={() => handleDeleteAuthorisedUser(deleteUserConfirmId)}
              disabled={!!deletingUserId}
              startIcon={deletingUserId ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
              sx={{ textTransform: 'none' }}
            >
              {deletingUserId ? 'Removing…' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>

    {/* Smart Locks Card */}
    <Box sx={{ ...TenantDashboardStyles.card, flex: isSubscriber ? 1 : 2, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={TenantDashboardStyles.cardTitle}>
          <LockIcon /> Smart Locks
        </Typography>
        <Tooltip title="Refresh lock status">
          <IconButton onClick={fetchLocks} size="small" disabled={locksLoading}>
            <RefreshIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <SmartLocksList propertyId={propertyId} onGenerateAccessCode={handleGenerateAccessCode} />
    </Box>

  </Box>{/* end Row 1 */}

  {/* ── Row 2: Bottom two cards, each 50% width ──────────────────────── */}
  <Box sx={{
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: '24px',
    alignItems: 'stretch',
  }}>

    {/* Access Code Management Card */}
    <Box sx={{ ...TenantDashboardStyles.card, flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={TenantDashboardStyles.cardTitle}>
          <LockOpenIcon /> Access Code Management
        </Typography>
        <IconButton onClick={fetchAccessCodes} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>
      <Button
        variant="contained"
        fullWidth
        onClick={() => setShowCodeGeneratorModal(true)}
        disabled={locksLoading}
        sx={TenantDashboardStyles.primaryButton}
      >
        Generate Access Code
      </Button>
      {activeCode && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Current Active Permanent Code:
          </Typography>
          <Box sx={TenantDashboardStyles.accessCodeContainer}>
            <Box sx={TenantDashboardStyles.accessCodeDisplay}>
              {activeCode.code}
              <Tooltip title="Copy code">
                <IconButton onClick={() => handleCopyCode(activeCode.code)} sx={TenantDashboardStyles.copyButton}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography sx={TenantDashboardStyles.expiryText}>
              Expires in:{' '}
              <span style={{ color: countdown === 'Expired' ? '#333333' : '#666666', fontWeight: 600 }}>
                {countdown}
              </span>
            </Typography>
          </Box>
        </Box>
      )}
      {!activeCode && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography sx={TenantDashboardStyles.noCodeMessage}>
            No active access codes. Generate one to provide property access.
          </Typography>
        </Box>
      )}
    </Box>

    {/* Scheduled Access Code Card */}
    <Box sx={{ ...TenantDashboardStyles.card, flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={TenantDashboardStyles.cardTitle}>
          <AccessTimeIcon /> Scheduled Access Code
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generate a time-limited access code for a guest, contractor, or service provider and send it via email or SMS.
      </Typography>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="timed-lock-select-label">Select Smart Lock</InputLabel>
        <Select
          labelId="timed-lock-select-label"
          value={timedLockId || ''}
          label="Select Smart Lock"
          onChange={(e) => setTimedLockId(e.target.value)}
        >
          {locks && locks.length > 0 ? (
            locks.map((lock) => (
              <MenuItem key={lock.lockId} value={lock.lockId}>
                {lock.deviceName || `Lock ${lock.lockId.slice(0, 8)}...`}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled value="">No locks available</MenuItem>
          )}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        fullWidth
        onClick={() => { setSuccessMessage(''); setTimedCodeError(null); setShowTimedCodeDialog(true); }}
        disabled={!timedLockId || locksLoading}
        startIcon={<SendIcon />}
        sx={TenantDashboardStyles.primaryButton}
      >
        Schedule & Send Access Code
      </Button>
      <Box sx={{ mt: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Scheduled Codes ({guestCodes.length})
          </Typography>
          <IconButton size="small" onClick={fetchAccessCodes}><RefreshIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
        {guestCodes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
            <AccessTimeIcon sx={{ fontSize: 22, color: 'text.disabled', display: 'block', mx: 'auto', mb: 0.5 }} />
            <Typography variant="caption" color="text.disabled">No scheduled guest codes yet</Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25, pr: 0.5 }}>
            {guestCodes.map((c, idx) => {
              const now = Date.now();
              const expiresMs = new Date(c.expiresAt).getTime();
              const createdMs = new Date(c.createdAt).getTime();
              const diff = expiresMs - now;
              const isExpired = diff <= 0;
              const isUpcoming = now < createdMs;
              const status = isExpired ? 'expired' : isUpcoming ? 'upcoming' : 'active';
              const countdownStr = formatCountdown(diff);
              const displayName = c.name
                ? c.name.split('-').filter(p => !p.match(/^[A-Z0-9]{6}$/) && !p.match(/Tenant/)).join(' ').trim() || c.name
                : 'Guest';
              const statusStyle = {
                active:   { border: '#4caf50', bg: '#f1f8f1', chip: '#4caf50',  chipText: '#fff'  },
                upcoming: { border: '#2196f3', bg: '#f0f6ff', chip: '#2196f3',  chipText: '#fff'  },
                expired:  { border: '#e0e0e0', bg: '#fafafa', chip: '#bdbdbd',  chipText: '#666'  },
              }[status];
              return (
                <Box key={c.codeId || idx} sx={{ p: 1.75, borderRadius: 2, border: `1.5px solid ${statusStyle.border}`, bgcolor: statusStyle.bg, opacity: isExpired ? 0.65 : 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: 17, fontWeight: 700, letterSpacing: '2px', color: isExpired ? '#999' : '#1a1a2e' }}>
                        {c.code || '——'}
                      </Typography>
                      {c.code && (
                        <Tooltip title="Copy code">
                          <IconButton size="small" onClick={() => handleCopyCode(c.code)} sx={{ color: isExpired ? '#ccc' : '#555' }}>
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <Chip label={status.charAt(0).toUpperCase() + status.slice(1)} size="small"
                      sx={{ fontSize: 10, height: 20, fontWeight: 700, bgcolor: statusStyle.chip, color: statusStyle.chipText }} />
                  </Box>
                  {displayName && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                      👤 {displayName}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600, color: isExpired ? '#bbb' : status === 'upcoming' ? '#2196f3' : '#4caf50' }}>
                    ⏱ {isExpired ? 'Expired' : isUpcoming ? `Starts in ${countdownStr}` : `Expires in ${countdownStr}`}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>

  </Box>{/* end Row 2 */}

</Box>{/* end Main Layout */}

            {/* ── Payment Modal ──────────────────────────────────────────────── */}
            <Modal
                open={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Paper sx={{ width: '100%', maxWidth: 500, p: 4, outline: 'none', borderRadius: '16px' }}>
                    <TenantPaymentForm
                        email={tenantId}
                        propertyId={propertyId}
                        customerId={subscription?.customerId}
                        onSuccess={() => {
                            setShowPaymentModal(false);
                            fetchSubscription();
                            setSnackbar({ open: true, message: 'Subscription successful!' });
                        }}
                        onError={(err) => setError(err)}
                    />
                </Paper>
            </Modal>

            {/* ── Timed Access Code Dialog ───────────────────────────────────── */}
            <Dialog
                open={showTimedCodeDialog}
                onClose={() => {
                    if (!generatingCode && !sendingEmail) {
                        setShowTimedCodeDialog(false);
                        resetTimedCodeForm();
                    }
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Schedule & Send Access Code</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>

                        {/* Success Message */}
                        {successMessage && (
                            <Alert severity="success">{successMessage}</Alert>
                        )}

                        {/* Error Message */}
                        {timedCodeError && (
                            <Alert severity="error" onClose={() => setTimedCodeError(null)}>
                                {timedCodeError}
                            </Alert>
                        )}

                        {/* Recipient Information */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Recipient Information
                        </Typography>
                        <TextField
                            label="Receiver Email"
                            type="email"
                            fullWidth
                            value={receiverEmail}
                            onChange={(e) => setReceiverEmail(e.target.value.trim())}
                            helperText="Email address of the person receiving access"
                            error={!!receiverEmail && !receiverEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)}
                        />
                        <TextField
                            label="Receiver Phone (Optional)"
                            type="tel"
                            fullWidth
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            helperText="Phone number for SMS notification"
                        />
                        <TextField
                            label="Receiver Name (Optional)"
                            fullWidth
                            value={receiverName}
                            onChange={(e) => setReceiverName(e.target.value)}
                            helperText="Name of the person receiving access"
                        />

                        {/* Access Time Window */}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Access Time Window
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Start Date"
                                type="date"
                                fullWidth
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                            <TextField
                                label="Start Time"
                                type="time"
                                fullWidth
                                value={startTime}
                                onChange={(e) => setStartTime(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="End Date"
                                type="date"
                                fullWidth
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                            <TextField
                                label="End Time"
                                type="time"
                                fullWidth
                                value={endTime}
                                onChange={(e) => setEndTime(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Box>

                        {/* Selected Lock Info */}
                        {timedLockId && (
                            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Selected Lock:{' '}
                                    <strong>{locks.find(l => l.lockId === timedLockId)?.deviceName || 'Smart Lock'}</strong>
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowTimedCodeDialog(false);
                            resetTimedCodeForm();
                        }}
                        disabled={generatingCode || sendingEmail}
                        sx={{ ...TenantDashboardStyles.secondaryButton, marginTop: "16px" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleGenerateTimedAccessCode}
                        variant="contained"
                        disabled={generatingCode || sendingEmail}
                        startIcon={generatingCode || sendingEmail ? <CircularProgress size={20} /> : <SendIcon />}
                        sx={TenantDashboardStyles.primaryButton}
                    >
                        {generatingCode ? 'Generating...' : sendingEmail ? 'Sending...' : 'Generate & Send Code'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
            />

            <AddAuthorizedUserDialog
                open={addUserDialogOpen}
                onClose={() => setAddUserDialogOpen(false)}
                propertyId={propertyId}
                addedBy={tenantId}
                onUserAdded={(newUser) => {
                    setAuthorisedUsers((prev) => [newUser, ...prev]);
                    setSnackbar({ open: true, message: `${newUser.email} added as authorised user!` });
                }}
            />

            <AccessCodeGeneratorModal
                open={showCodeGeneratorModal}
                onClose={() => setShowCodeGeneratorModal(false)}
                locks={locks}
                tenantId={tenantId}
                propertyId={propertyId}
                ownerEmail={ownerEmail}
                ownerName={ownerName}
                agentDashboardUrl={`${window.location.origin}/agent-dashboard`}
                onSuccess={(msg) => {
                    setSnackbar({ open: true, message: msg });
                    fetchAccessCodes();
                }}
            />`     `
        </Box>
    );
};

export default TenantDashboard;